import { useState } from "react";
import { useSitePages } from "../pages/nav";
import { REPEATABLE_PAGES } from "../pages/registry";
import { pageOfInstance } from "../pages/repeatable";
import { REPEATABLE_STEP } from "../routes/registry";
import { CUSTOM_CSS_KEY, labelForTarget } from "../settings/catalog";
import { parseRules, withoutRule } from "../settings/css";
import { useSettings } from "../settings/context";
import type { CatalogContext } from "../settings/types";
import { AppearanceModal } from "./Apparence";
import { Aimer, type Aim } from "./Viseur";

/**
 * CE QUI S'EST ACCUMULÉ, ET DE QUOI EN RETIRER UNE.
 *
 * ⚠️ SANS CETTE LIGNE, RIEN NE S'ANNULE. Une règle qui casse la mise en page ne se
 * retirerait que depuis le code — c'est-à-dire par un déploiement, pour un événement,
 * ce que cette fonctionnalité existe précisément pour éviter. C'est ce retrait qui la
 * rend utilisable plutôt qu'inquiétante.
 *
 * Et c'est le seul endroit d'où l'on voit l'ensemble : la carte ne montre jamais que le
 * bloc qu'on survole.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function CssRules() {
  const { value, write, saving, typos, forms } = useSettings();
  const { name: nameOf } = useSitePages();
  const raw = value(CUSTOM_CSS_KEY);
  const feuille = typeof raw === "string" ? raw : "";
  const rules = parseRules(feuille);

  const [aimOpen, setAimOpen] = useState(false);
  const [target, setTarget] = useState<Aim | null>(null);

  /* Le contexte de l'événement, pour que `labelForTarget` nomme AUSSI les blocs engendrés
     par typologie (« Typologies proposées ») : sans typos, leur cible n'a pas de nom et la
     règle s'affiche par son sélecteur brut. */
  const ctx: CatalogContext = {
    typos: typos.flatMap((t) =>
      t.id === null ? [] : [{ id: t.id, name: t.name }],
    ),
    forms,
  };
  /**
   * L'EXEMPLAIRE QUI BORNE LA RÈGLE (`[data-page="infos_2"]`, voir `selectorOf`), par
   * son nom. Sans lui, deux règles sur le titre de deux sections se liraient toutes
   * deux « Section », et rien ne dirait laquelle retirer.
   */
  const pageName = (key: string): string => {
    for (const base of REPEATABLE_PAGES) {
      if (key === base.key) return nameOf(pageOfInstance(base));
      if (key.startsWith(`${base.key}_`))
        return nameOf(pageOfInstance(base, key.slice(base.key.length + 1)));
    }
    const step = REPEATABLE_STEP;
    if (step?.stepName && key === step.key) return step.stepName;
    if (step?.stepName && key.startsWith(`${step.key}_`))
      return `${step.stepName} ${key.slice(step.key.length + 1)}`;
    return key;
  };
  const blockName = (selector: string): string | null => {
    const page = /\[data-page="([^"]+)"\]/.exec(selector)?.[1];
    const anchor = /\[data-cfg="([^"]+)"\]/.exec(selector)?.[1];
    const block = anchor ? labelForTarget(anchor, ctx) : null;
    if (anchor ? !block : !page) return null;
    const name = [block, page ? pageName(page) : null].filter(Boolean).join(" · ");
    // Ce qui suit l'ancre : « h2 », « .fcase »… Il dit sur QUOI dans le bloc ça porte.
    const remaining = selector
      .replace(/\[data-page="[^"]+"\]/, "")
      .replace(/\[data-cfg="[^"]+"\]/, "")
      .trim();
    return remaining ? `${name} — ${remaining}` : name;
  };

  return (
    <div className="css__l">
      {rules.length === 0 && (
        <p className="row__h">
          Aucune règle pour l'instant.
        </p>
      )}

      {rules.map((r) => (
        <div className="css__r" key={r.selector}>
          {/* Le NOM du bloc quand on le reconnaît, le sélecteur sinon : « Titre de
              l'étape » se relit, `[data-cfg="infoTitle"] h2` demande un effort. Le
              sélecteur reste dans l'infobulle, pour qui veut vérifier. */}
          <span className="css__s" title={`${r.selector} { ${r.body} }`}>
            {blockName(r.selector) ?? r.selector}
          </span>
          <button
            type="button"
            className="fed__c fed__del"
            aria-label={`Retirer la règle sur ${blockName(r.selector) ?? r.selector}`}
            title="Retirer cette règle"
            onClick={() => write(CUSTOM_CSS_KEY, withoutRule(feuille, r.selector))}
          >
            ✕
          </button>
        </div>
      ))}

      {/* ⚠️ LA SECONDE PORTE, ET ELLE RÉPOND À UNE AUTRE QUESTION. La carte au survol
          ne donne accès qu'au BLOC entier qu'on survole ; celle-ci laisse viser
          l'élément exact — un mot du pied de page, un bouton, une case. */}
      <button
        type="button"
        className="appa__o"
        disabled={!saving}
        title={
          saving
            ? "Désigner un élément de la page à personnaliser"
            : "Indisponible : cet écran n'est relié à aucun événement."
        }
        onClick={() => setAimOpen(true)}
      >
        ✎ Personnaliser un élément
      </button>

      {aimOpen && (
        <Aimer
          onCancel={() => setAimOpen(false)}
          onAim={(v) => {
            setAimOpen(false);
            setTarget(v);
          }}
        />
      )}

      {target && (
        <AppearanceModal
          open
          title={blockName(target.selector) ?? target.selector}
          selector={target.selector}
          element={target.element}
          onClose={() => setTarget(null)}
        />
      )}
    </div>
  );
}
